import {
	musicIdSchema,
	type MusicLibrary,
	type MusicMood,
	type MusicQueue,
	type MusicTrack
} from '@andresot/protocol/music';
import * as api from '$api/music';
import { copyText } from '$utils/clipboard';
import { toasts } from './toasts.svelte';

export const musicTime = (seconds: number) =>
	`${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
class MusicStore {
	queue = $state<MusicTrack[]>([]);
	index = $state(0);
	position = $state(0);
	duration = $state(0);
	playing = $state(false);
	loading = $state(false);
	expanded = $state(false);
	error = $state('');
	blocked = $state(false);
	shuffle = $state(false);
	repeat = $state<MusicQueue['repeat']>('off');
	wave = $state(false);
	mood = $state<MusicMood>('personal');
	library = $state<MusicLibrary | null>(null);
	current = $derived(this.queue[this.index] ?? null);
	private audio: HTMLAudioElement | null = null;
	private userId: string | null = null;
	private generation = 0;
	private accountGeneration = 0;
	private failures = 0;
	private recorded = false;
	private lastSaved = 0;
	private activeSrc = '';
	private pendingSeek = 0;
	private pendingLikes = new Set<string>();

	attach(audio: HTMLAudioElement) {
		this.audio = audio;
	}
	detach() {
		this.pause();
		this.audio?.removeAttribute('src');
		this.audio?.load();
		this.audio = null;
	}
	account(id: string | null) {
		if (this.userId === id) return;
		this.pause();
		this.accountGeneration++;
		this.audio?.removeAttribute('src');
		this.audio?.load();
		this.activeSrc = '';
		this.queue = [];
		this.position = 0;
		this.index = 0;
		this.duration = 0;
		this.pendingSeek = 0;
		this.shuffle = false;
		this.repeat = 'off';
		this.wave = false;
		this.mood = 'personal';
		this.failures = 0;
		this.library = null;
		this.error = '';
		this.expanded = false;
		this.userId = id;
		if (typeof navigator !== 'undefined' && navigator.mediaSession)
			navigator.mediaSession.metadata = null;
		if (!id) return;
		try {
			const raw = JSON.parse(localStorage.getItem(`andresot:music:${id}`) ?? 'null');
			if (!raw || !Array.isArray(raw.ids) || raw.ids.length > 200) return;
			const ids = raw.ids.filter(
				(value: unknown) => musicIdSchema.safeParse(value).success
			) as string[];
			const currentId =
				ids[Math.min(Math.max(0, Math.floor(Number(raw.index) || 0)), ids.length - 1)];
			if (!currentId) return;
			// Восстановление не доверяет метаданным из localStorage и не запускает звук.
			const epoch = this.accountGeneration;
			const restoreGeneration = this.generation;
			void api
				.fetchMusicTrack(currentId)
				.then(async ({ track }) => {
					if (epoch !== this.accountGeneration || restoreGeneration !== this.generation) return;
					this.queue = [track];
					this.index = 0;
					this.position = Math.max(0, Math.min(Number(raw.position) || 0, track.duration));
					this.duration = track.duration;
					this.shuffle = raw.shuffle === true;
					this.repeat = ['off', 'all', 'one'].includes(raw.repeat) ? raw.repeat : 'off';
					this.wave = raw.wave === true;
					// Остальная очередь восстанавливается по сохранённым данным каталога одним запросом.
					const saved = await api.restoreMusicQueue(ids);
					if (epoch !== this.accountGeneration || restoreGeneration !== this.generation) return;
					this.queue = saved.tracks;
					this.index = Math.max(
						0,
						saved.tracks.findIndex((t) => t.id === currentId)
					);
				})
				.catch(() => {
					/* Недоступная сохранённая очередь не мешает открыть каталог. */
				});
		} catch {
			/* Повреждённые данные устройства можно безопасно забыть. */
		}
	}
	persist() {
		if (!this.userId) return;
		try {
			localStorage.setItem(
				`andresot:music:${this.userId}`,
				JSON.stringify({
					ids: this.queue.slice(0, 200).map((t) => t.id),
					index: this.index,
					position: this.position,
					shuffle: this.shuffle,
					repeat: this.repeat,
					wave: this.wave
				})
			);
		} catch {
			/* Нет места — сохраняется серверная коллекция. */
		}
	}
	async openTrack(id: string) {
		this.pause();
		const epoch = this.generation;
		try {
			const { track } = await api.fetchMusicTrack(id);
			if (epoch !== this.generation) return;
			this.queue = [track];
			this.index = 0;
			this.position = 0;
			this.duration = track.duration;
			this.activeSrc = '';
			this.wave = false;
			this.expanded = true;
			this.persist();
		} catch {
			if (epoch === this.generation) this.error = 'Трек по ссылке недоступен';
		}
	}
	async refreshLibrary() {
		const epoch = this.accountGeneration;
		const library = await api.fetchMusicLibrary();
		if (epoch === this.accountGeneration) this.library = library;
		return library;
	}
	async start(tracks: MusicTrack[], index = 0, wave = false) {
		if (this.blocked) {
			this.error = 'Завершите звонок, запись или видео, чтобы включить музыку';
			return;
		}
		this.pause();
		const offset = index >= 200 ? index : 0;
		this.queue = tracks.slice(offset, offset + 200);
		this.index = Math.max(0, Math.min(index - offset, this.queue.length - 1));
		this.wave = wave;
		this.position = 0;
		this.failures = 0;
		this.activeSrc = '';
		await this.play();
	}
	async startWave() {
		if (this.blocked) {
			this.error = 'Завершите звонок, запись или видео';
			return;
		}
		const epoch = ++this.generation;
		this.loading = true;
		this.error = '';
		try {
			const page = await api.fetchMusicWave(this.mood);
			if (epoch !== this.generation) return;
			if (!page.tracks.length) {
				this.error = 'Пока нет новых треков. Измените настроение или жанры.';
				return;
			}
			await this.start(page.tracks, 0, true);
		} catch (error) {
			if (epoch === this.generation)
				this.error = error instanceof Error ? error.message : 'Не удалось собрать волну';
		} finally {
			if (epoch === this.generation) this.loading = false;
		}
	}
	async play() {
		const track = this.current,
			audio = this.audio;
		if (!track || !audio || !this.userId || this.blocked) return;
		const epoch = ++this.generation;
		this.loading = true;
		this.error = '';
		try {
			if (this.activeSrc !== track.id || !audio.src || audio.error) {
				const result = await api.fetchMusicPlayback(track.id);
				if (epoch !== this.generation || this.blocked) return;
				this.queue[this.index] = result.track;
				audio.src = result.streamUrl;
				this.activeSrc = track.id;
				this.recorded = false;
				this.duration = result.track.duration;
				// WebKit запрещает currentTime до loadedmetadata. Позицию применяет ready().
				this.pendingSeek = Math.min(this.position, Math.max(0, result.track.duration - 1));
			}
			document.querySelectorAll<HTMLMediaElement>('audio, video').forEach((other) => {
				if (other !== audio) other.pause();
			});
			await audio.play();
			if (epoch !== this.generation) return;
			this.playing = true;
			this.persist();
			this.metadata();
		} catch (error) {
			if (epoch !== this.generation) return;
			if (error instanceof DOMException && error.name === 'NotAllowedError')
				this.error = 'Нажмите воспроизведение ещё раз — браузер ожидает ваше разрешение';
			else if (!(error instanceof DOMException && error.name === 'AbortError')) await this.failed();
		} finally {
			if (epoch === this.generation) this.loading = false;
		}
	}
	pause() {
		this.generation++;
		this.audio?.pause();
		this.playing = false;
		this.loading = false;
		this.persist();
		if (typeof navigator !== 'undefined' && navigator.mediaSession)
			navigator.mediaSession.playbackState = 'paused';
	}
	toggle() {
		if (this.playing || this.loading) this.pause();
		else {
			this.failures = 0;
			void this.play();
		}
	}
	seek(value: number) {
		this.position = Math.max(0, Math.min(value, this.duration));
		if (this.audio && this.audio.readyState > 0 && this.activeSrc === this.current?.id)
			this.audio.currentTime = this.position;
		else this.pendingSeek = this.position;
		this.persist();
	}
	ready() {
		if (!this.audio) return;
		if (Number.isFinite(this.audio.duration)) this.duration = this.audio.duration;
		if (this.pendingSeek > 0)
			this.audio.currentTime = Math.min(this.pendingSeek, Math.max(0, this.duration - 1));
		this.pendingSeek = 0;
	}
	tick() {
		if (!this.audio) return;
		if (this.audio.readyState === 0) return;
		this.position = this.audio.currentTime;
		if (Number.isFinite(this.audio.duration)) this.duration = this.audio.duration;
		if (this.position > 1) this.failures = 0;
		if (this.position >= Math.min(10, this.duration / 2) && !this.recorded && this.current) {
			this.recorded = true;
			const epoch = this.accountGeneration;
			void api
				.musicFeedback(this.current.id, 'history')
				.then(() => {
					if (epoch === this.accountGeneration) void this.refreshLibrary().catch(() => undefined);
				})
				.catch(() => {
					if (epoch === this.accountGeneration)
						this.error = 'Историю не удалось сохранить. Проверьте соединение.';
				});
		}
		if (Date.now() - this.lastSaved > 5000) {
			this.persist();
			this.lastSaved = Date.now();
		}
	}
	async next(ended = false) {
		if (ended && this.repeat === 'one') {
			this.seek(0);
			await this.play();
			return;
		}
		let nextIndex = this.index + 1;
		if (this.shuffle && this.queue.length > 1)
			nextIndex =
				(this.index + 1 + Math.floor(Math.random() * (this.queue.length - 1))) % this.queue.length;
		if (nextIndex >= this.queue.length && this.wave) {
			const epoch = ++this.generation;
			this.loading = true;
			try {
				const page = await api.fetchMusicWave(
					this.mood,
					this.queue.map((t) => t.id)
				);
				if (epoch !== this.generation) return;
				if (page.tracks.length) {
					this.queue = page.tracks;
					nextIndex = 0;
				}
			} catch {
				if (epoch === this.generation) {
					this.pause();
					this.error = 'Не удалось продолжить волну. Повторите подключение.';
				}
				return;
			} finally {
				if (epoch === this.generation) this.loading = false;
			}
		}
		if (nextIndex >= this.queue.length) {
			if (this.repeat === 'all') nextIndex = 0;
			else {
				this.pause();
				return;
			}
		}
		this.pause();
		this.index = nextIndex;
		this.position = 0;
		this.activeSrc = '';
		await this.play();
	}
	previous() {
		if (this.position > 3) {
			this.seek(0);
			return;
		}
		this.pause();
		this.index = Math.max(0, this.index - 1);
		this.position = 0;
		this.activeSrc = '';
		void this.play();
	}
	async failed() {
		this.failures++;
		this.activeSrc = '';
		if (this.failures >= 3) {
			this.pause();
			this.error = 'Три трека подряд недоступны. Проверьте сеть и повторите подключение.';
			return;
		}
		this.error = 'Трек недоступен. Перехожу к следующему.';
		await this.next();
	}
	async like(track: MusicTrack, dislike = false) {
		if (this.pendingLikes.has(track.id)) return;
		this.pendingLikes.add(track.id);
		const epoch = this.accountGeneration;
		try {
			const enabled = dislike ? !track.disliked : !track.liked;
			await api.musicFeedback(track.id, dislike ? 'dislike' : 'like', enabled);
			if (epoch !== this.accountGeneration) return;
			const update = (t: MusicTrack) =>
				t.id !== track.id
					? t
					: {
							...t,
							...(dislike
								? { disliked: enabled, liked: enabled ? false : t.liked }
								: { liked: enabled, disliked: enabled ? false : t.disliked })
						};
			this.queue = this.queue.map(update);
			Object.assign(track, update(track));
			await this.refreshLibrary();
			if (dislike && enabled && this.current?.id === track.id) await this.next();
		} catch (error) {
			if (epoch === this.accountGeneration)
				toasts.error(error instanceof Error ? error.message : 'Не удалось сохранить');
		} finally {
			this.pendingLikes.delete(track.id);
		}
	}
	async share(track: MusicTrack) {
		try {
			await copyText(
				`${location.origin}/?section=services&service=music&track=${encodeURIComponent(track.id)}`
			);
			toasts.success('Ссылка на трек скопирована');
		} catch {
			toasts.error('Не удалось скопировать ссылку');
		}
	}
	close() {
		this.pause();
		this.queue = [];
		this.index = 0;
		this.position = 0;
		this.expanded = false;
		this.activeSrc = '';
		this.audio?.removeAttribute('src');
		this.audio?.load();
		this.persist();
		if (navigator.mediaSession) navigator.mediaSession.metadata = null;
	}
	metadata() {
		if (!navigator.mediaSession || !this.current) return;
		navigator.mediaSession.metadata = new MediaMetadata({
			title: this.current.title,
			artist: this.current.artist,
			album: `ANDRESOT · ${this.current.provider === 'archive' ? 'Internet Archive' : 'Audius'}`,
			artwork: this.current.artwork ? [{ src: this.current.artwork }] : []
		});
		navigator.mediaSession.playbackState = 'playing';
	}
}
export const music = new MusicStore();
